import { User } from 'common/components/icons/User';
import { Logo } from 'common/components/icons/Logo';
import { Logout } from 'common/components/icons/Logout';
import { useTranslation } from 'common/hooks/useTranslation';
import { useSyncToasts } from 'common/offline/useSyncToasts';
import { SyncStatusIndicator } from 'common/components/SyncStatusIndicator';
import { clientID, commitHash, logoutRedirectUrl, logoutUrl } from 'config';
import { FC, useMemo, useState } from 'react';

import { G } from '@mobily/ts-belt';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  AppBar,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { mergeStyles } from 'app/theme/materialTheme';
import { useLocation } from 'react-router-dom';
import { LanguagePicker } from '../../internationalization/components/LanguagePicker';
import { ClearCacheDialog } from './ClearCacheDialog';
import { useClearCache } from '../hooks/useClearCache';
import * as styles from './AppTopbar.styles';

const appLogoutUrl = `${logoutUrl}?client_id=${clientID}&post_logout_redirect_uri=${logoutRedirectUrl}`;

export const AppTopbar: FC = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const { clearCache } = useClearCache();

  // Enable sync toast notifications
  useSyncToasts();

  const currentLocation = useLocation();

  const borderBottomConditional = useMemo(() => {
    if (currentLocation.pathname.length < 2) {
      return {};
    }
    return currentLocation.pathname.includes('radio') ? styles.topbarRadioNetwork : styles.topbarNetwork;
  }, [currentLocation.pathname]);

  const handleClearCacheClick = () => {
    setAnchorEl(null);
    setClearCacheDialogOpen(true);
  };

  const handleConfirmClear = async () => {
    try {
      await clearCache();
      toast.success(t('cache.clear.success'));
    } catch {
      toast.error(t('cache.clear.error'));
    }
  };

  return (
    <AppBar sx={mergeStyles([styles.topbar, borderBottomConditional])}>
      <Grid>
        <Logo height={styles.topbarLogo.height} />
      </Grid>
      <Grid sx={styles.topbarRight}>
        <SyncStatusIndicator />
        <LanguagePicker />
        <IconButton
          data-cy="user-button"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
        >
          <User />
        </IconButton>
        <Menu anchorEl={anchorEl} open={G.isNotNullable(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem dense onClick={handleClearCacheClick}>
            <ListItemIcon>
              <DeleteForeverIcon />
            </ListItemIcon>
            <ListItemText>{t('cache.clear.menuItem')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem dense sx={styles.menuItemError} onClick={() => location.replace(appLogoutUrl)}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText>{t('login.logout.menuOption')}</ListItemText>
          </MenuItem>
          <MenuItem disabled dense>
            <Typography flexWrap="wrap" variant="caption">
              ver: {(commitHash ?? 'N/A').slice(0, 8)}
            </Typography>
          </MenuItem>
        </Menu>
        <ClearCacheDialog
          open={clearCacheDialogOpen}
          onClose={() => setClearCacheDialogOpen(false)}
          onConfirm={handleConfirmClear}
        />
      </Grid>
    </AppBar>
  );
};
